import React, { FC, ReactNode, useMemo } from 'react';
import dynamic from 'next/dynamic';

import { toObservedLoadingComponent } from '~/app/components/ItemPage/components/LoadingObserver';
import ItemPageMetadata from '~/app/components/ItemPage/components/ItemPageMetadata';
import ItemPageHeader from '~/app/components/ItemPage/components/ItemPageHeader';
import { CORE_SECTION_COMPONENTS } from '~/app/components/ItemPage/constants';
import { PageSectionTypes } from '~/app/components/ItemPage/sections/types';
import useSelectCustomPage from '~/app/lib/hooks/useSelectCustomPage';
import { useAppLoading } from '~/app/components/NextApp/lib/CoreUi';
import { PAGE_HEADER_HEIGHT } from '~/app/components/PageHeader';
import { PageTrackerContext } from '~/app/components/Page';
import ItemPage, { ItemPageProps } from '~/app/components/ItemPage';
import { PageTypes } from '~/types';

import { CustomPageActions } from './components/CustomPageActions';
import useCustomPageContext from './useCustomPageContext';
import toStructuredData from './toStructuredData';

import {
  PageSectionElement,
  PageSections,
} from '~/app/components/ItemPage/components/SectionRenderer';

const OrchardLegalFooterDynamic = dynamic(
  () => import('~/app/components/ItemPage/components/OrchardLegalFooter'),
  { ssr: true }
);

const VideosSectionDynamic = dynamic(
  () => import('~/app/components/ItemPage/sections/VideosSection')
);

const MerchSectionDynamic = dynamic(
  () => import('~/app/components/ItemPage/sections/MerchSection'),
  {
    loading: toObservedLoadingComponent(),
  }
);

const ShowsSectionDynamic = dynamic(
  () => import('~/app/components/ItemPage/sections/ShowsSection'),
  {
    loading: toObservedLoadingComponent(),
  }
);

const SECTION_COMPONENTS = {
  ...CORE_SECTION_COMPONENTS,

  [PageSectionTypes.VIDEOS]: VideosSectionDynamic,
  [PageSectionTypes.MERCH]: MerchSectionDynamic,
  [PageSectionTypes.SHOWS]: ShowsSectionDynamic,
};

export interface CustomPageProps
  extends Pick<ItemPageProps, 'withRedirectIfNeeded'> {
  customPageId: number;

  beforeHeader?: {
    height: string;
    content: ReactNode;
  };
}

const CustomPage: FC<CustomPageProps> = ({
  customPageId,
  beforeHeader,
  withRedirectIfNeeded,
}) => {
  const customPage = useSelectCustomPage(customPageId)!;
  const itemContext = useCustomPageContext({ customPage })!;

  useAppLoading()(customPage.isLoading);

  return (
    <ItemPage
      key={customPageId}
      pageType={PageTypes.CUSTOM}
      hasContent={Boolean(customPage.name)} // TODO: Add useful check
      testId="customPage"
      error={customPage.error}
      itemContext={itemContext}
      isLoading={!!customPage.isLoading}
      isOwned={customPage.isOwned}
      isDraft={customPage.isDraft}
      pageBrand={customPage.pageBrand}
      pageOwnedByAccountIds={customPage.ownedByAccountIds}
      trackerContext={useMemo(
        (): PageTrackerContext => ({
          artistId: customPage.artistId,
          artistName: customPage.artistName,

          customPageId: customPage.id,
          customPageName: customPage.name,
        }),
        [customPage]
      )}
      withRedirectIfNeeded={withRedirectIfNeeded}
      pagePath={customPage.pagePath}
      header={useMemo(() => {
        return {
          height: `calc(${PAGE_HEADER_HEIGHT} + ${
            beforeHeader?.height ?? '0rem'
          })`,

          content: (
            <>
              {beforeHeader?.content}
              <ItemPageHeader
                pagePath={customPage.pagePath}
                userCanEdit={customPage.userCanEdit}
                renderActions={({ isOpen, actionButtonRef, onClose }) => {
                  return (
                    <CustomPageActions
                      itemContext={itemContext}
                      isOpen={isOpen}
                      actionButtonRef={actionButtonRef}
                      onClose={onClose}
                    />
                  );
                }}
              />
            </>
          ),
        };
      }, [beforeHeader, customPage])}
      content={useMemo(() => {
        return (
          <>
            <PageSections
              items={itemContext.layout.main}
              id=""
              components={SECTION_COMPONENTS}
              itemContext={itemContext}
              withTopMargin={false}
              groupSections={[
                [PageSectionTypes.PAGE_TITLE, PageSectionTypes.ICON_LINKS],
              ]}
            />
            <PageSectionElement>
              <OrchardLegalFooterDynamic />
            </PageSectionElement>
          </>
        );
      }, [itemContext])}
    >
      <ItemPageMetadata
        error={customPage.error}
        pagePath={customPage.pagePath}
        itemContext={itemContext}
        isOwned={customPage.isOwned}
        defaultTitle={customPage.name}
        defaultDescription={customPage.name}
        // REVIEW: this could be a <StructuredData> component instead
        // of having to pass props down the tree to <PageMetaData>
        // which is getting quite overloaded now
        toStructuredData={(params) =>
          // @ts-ignore
          toStructuredData({
            ...params,
            customPageName: customPage.name,
            artistName: customPage.artistName,
            artistPagePath: customPage.artistPagePath,
          })
        }
      />
    </ItemPage>
  );
};

export default CustomPage;
